import React, { useState, useEffect } from 'react';
import { getToken } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useAudioAnalysis } from '../contexts/AudioAnalysisContext';
import './AudioAnalysis.css';

const AudioAnalysis = () => {
  const { user } = useAuth();
  const isAdmin = user?.tier === 'admin';
  const {
    pendingAnalysis,
    completedAnalysis,
    analysisError,
    uploadProgress: contextUploadProgress,
    isAnalyzing,
    startAnalysis,
    cancelAnalysis,
    clearCompletedAnalysis,
    clearError
  } = useAudioAnalysis();

  const [file, setFile] = useState(null);
  const [showAllUsers, setShowAllUsers] = useState(false); // Admin: show all users' history
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, title
  const [passwordModal, setPasswordModal] = useState({ show: false, itemId: null, mode: 'unlock' }); // mode: unlock, set, change
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [speakerNames, setSpeakerNames] = useState({}); // { '화자1': '홍길동', '화자2': '김철수' }
  const [editingSpeaker, setEditingSpeaker] = useState(null);

  const ACCEPTED_FORMATS = ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/x-m4a', 'audio/m4a', 'audio/webm', 'audio/ogg', 'audio/aac', 'video/mp4'];
  const ACCEPTED_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.webm', '.ogg', '.aac', '.mp4'];
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type (check MIME type or file extension)
    const fileExt = '.' + selectedFile.name.split('.').pop().toLowerCase();
    const isValidType = ACCEPTED_FORMATS.includes(selectedFile.type) || ACCEPTED_EXTENSIONS.includes(fileExt);

    if (!isValidType) {
      setError('지원하지 않는 파일 형식입니다. mp3, wav, m4a, webm, ogg 파일만 업로드 가능합니다.');
      setFile(null);
      return;
    }

    // Validate file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('파일 크기가 너무 큽니다. 최대 100MB까지 업로드 가능합니다.');
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError(null);
    setResults(null);
  };

  // Fetch analysis history on component mount or when showAllUsers changes
  useEffect(() => {
    fetchHistory();
  }, [showAllUsers]);

  // Handle completed analysis from context (background analysis)
  useEffect(() => {
    if (completedAnalysis) {
      setResults(completedAnalysis);
      setShowTranscript(false);
      clearCompletedAnalysis();
      fetchHistory(); // Refresh history
      // Clear file after completion
      setFile(null);
    }
  }, [completedAnalysis, clearCompletedAnalysis]);

  // Handle analysis error from context
  useEffect(() => {
    if (analysisError) {
      setError(analysisError);
      clearError();
    }
  }, [analysisError, clearError]);

  const fetchHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const token = getToken();
      const endpoint = showAllUsers && isAdmin
        ? '/api/audio/admin/history'
        : '/api/audio/history';

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setHistory(data.data || []);
        }
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleHistoryItemClick = (item) => {
    // Check if item has password protection
    if (item.hasPassword) {
      setPasswordModal({ show: true, itemId: item.id, mode: 'unlock' });
      return;
    }

    setResults(item.results);
    setSelectedItem(item);
    setShowTranscript(false);
    // Scroll to results
    setTimeout(() => {
      document.querySelector('.results-container')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const handlePasswordSubmit = async () => {
    const { itemId, mode } = passwordModal;

    // Validate password (4 digits)
    if (!/^\d{4}$/.test(passwordInput)) {
      alert('4자리 숫자를 입력해주세요.');
      return;
    }

    // Validate password confirmation for set/change modes
    if (mode !== 'unlock') {
      if (passwordInput !== passwordConfirm) {
        alert('비밀번호가 일치하지 않습니다.');
        return;
      }
    }

    try {
      const token = getToken();

      if (mode === 'unlock') {
        // Verify password via GET with query parameter
        const response = await fetch(`/api/audio/history/${itemId}?password=${passwordInput}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const item = history.find(h => h.id === itemId);
          setResults(item.results);
          setSelectedItem(item);
          setShowTranscript(false);
          setPasswordModal({ show: false, itemId: null, mode: 'unlock' });
          setPasswordInput('');

          // Scroll to results
          setTimeout(() => {
            document.querySelector('.results-container')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }, 100);
        } else if (response.status === 403) {
          alert('비밀번호가 일치하지 않습니다.');
        } else {
          alert('비밀번호 확인에 실패했습니다.');
        }
      } else {
        // Set or change password via PUT
        const response = await fetch(`/api/audio/history/${itemId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ password: passwordInput })
        });

        if (response.ok) {
          // Update local history state
          setHistory(prev => prev.map(item =>
            item.id === itemId ? { ...item, hasPassword: true } : item
          ));
          // Update selected item if it's the one being edited
          if (selectedItem && selectedItem.id === itemId) {
            setSelectedItem(prev => ({ ...prev, hasPassword: true }));
          }
          setPasswordModal({ show: false, itemId: null, mode: 'unlock' });
          setPasswordInput('');
          setPasswordConfirm('');
          alert(mode === 'set' ? '비밀번호가 설정되었습니다.' : '비밀번호가 변경되었습니다.');
        } else {
          alert('비밀번호 설정에 실패했습니다.');
        }
      }
    } catch (err) {
      console.error('Password operation error:', err);
      alert('오류가 발생했습니다.');
    }
  };

  const handlePasswordModalClose = () => {
    setPasswordModal({ show: false, itemId: null, mode: 'unlock' });
    setPasswordInput('');
    setPasswordConfirm('');
  };

  const handleSetPassword = (e, itemId) => {
    e.stopPropagation();
    const item = history.find(h => h.id === itemId);
    setPasswordModal({
      show: true,
      itemId,
      mode: item.hasPassword ? 'change' : 'set'
    });
  };

  const handleEditTitle = (e, item) => {
    e.stopPropagation();
    setEditingId(item.id);
    setEditingTitle(item.title || '');
  };

  const handleSaveTitle = async (e, itemId) => {
    e.stopPropagation();
    try {
      const token = getToken();
      const response = await fetch(`/api/audio/history/${itemId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: editingTitle })
      });

      if (response.ok) {
        // Update local history state
        setHistory(prev => prev.map(item =>
          item.id === itemId ? { ...item, title: editingTitle } : item
        ));
        // Update selected item if it's the one being edited
        if (selectedItem && selectedItem.id === itemId) {
          setSelectedItem(prev => ({ ...prev, title: editingTitle }));
        }
      }
    } catch (err) {
      console.error('Failed to save title:', err);
    } finally {
      setEditingId(null);
      setEditingTitle('');
    }
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingId(null);
    setEditingTitle('');
  };

  const handleDeleteItem = async (e, itemId) => {
    e.stopPropagation();
    if (!window.confirm('이 분석 결과를 삭제하시겠습니까?')) return;

    try {
      const token = getToken();
      const response = await fetch(`/api/audio/history/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setHistory(prev => prev.filter(item => item.id !== itemId));
        if (selectedItem && selectedItem.id === itemId) {
          setSelectedItem(null);
          setResults(null);
        }
      }
    } catch (err) {
      console.error('Failed to delete item:', err);
    }
  };

  // Filter and sort history
  const filteredAndSortedHistory = (() => {
    // First filter
    let filtered = history.filter(item => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      const title = (item.title || '').toLowerCase();
      const filename = (item.filename || '').toLowerCase();
      const summary = (item.results?.summary || '').toLowerCase();
      const keywords = (item.results?.keywords || []).join(' ').toLowerCase();
      return title.includes(query) || filename.includes(query) || summary.includes(query) || keywords.includes(query);
    });

    // Then sort
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'title':
          const titleA = (a.title || a.filename || '').toLowerCase();
          const titleB = (b.title || b.filename || '').toLowerCase();
          return titleA.localeCompare(titleB);
        case 'newest':
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });
  })();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('파일을 선택해주세요.');
      return;
    }

    setError(null);

    // Use context's startAnalysis for background processing
    try {
      await startAnalysis(file);
    } catch (err) {
      // Error is handled by context and useEffect
      console.error('Audio analysis error:', err);
    }
  };

  const formatTimestamp = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Extract unique speakers from transcript
  const getUniqueSpeakers = () => {
    if (!results?.transcript || !Array.isArray(results.transcript)) return [];
    const speakers = new Set();
    results.transcript.forEach(item => {
      if (item.speaker) speakers.add(item.speaker);
    });
    return Array.from(speakers);
  };

  // Get display name for speaker
  const getSpeakerDisplayName = (speaker) => {
    return speakerNames[speaker] || speaker;
  };

  // Handle speaker name change
  const handleSpeakerNameChange = (originalName, newName) => {
    setSpeakerNames(prev => ({
      ...prev,
      [originalName]: newName.trim() || originalName
    }));
    setEditingSpeaker(null);
  };

  // Reset speaker names when results change
  useEffect(() => {
    if (results) {
      setSpeakerNames({});
    }
  }, [results?.id]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="audio-analysis">
      <header className="page-header">
        <h1>음성 파일 분석</h1>
        <p>음성 파일을 업로드하여 텍스트로 변환하고 요약을 생성합니다</p>
      </header>

      <div className="analysis-container">
        {/* Upload Form */}
        <form onSubmit={handleSubmit} className="upload-form">
          <div className="form-section">
            <label className="form-label">음성 파일 업로드</label>
            <div className="file-input-wrapper">
              <input
                type="file"
                accept=".mp3,.wav,.m4a,.aac,.webm,.ogg,.mp4,audio/*,video/mp4"
                onChange={handleFileChange}
                disabled={isAnalyzing}
                className="file-input"
                id="audioFile"
              />
              <label htmlFor="audioFile" className="file-input-label">
                <span className="file-icon">📁</span>
                <span className="file-text">
                  {file ? file.name : '파일 선택 (mp3, wav, m4a, webm, ogg)'}
                </span>
              </label>
            </div>
            {file && (
              <div className="file-info">
                <span className="file-size">{formatFileSize(file.size)}</span>
              </div>
            )}
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {isAnalyzing && contextUploadProgress > 0 && contextUploadProgress < 100 && (
            <div className="progress-section">
              <div className="progress-label">업로드 중... {contextUploadProgress}%</div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${contextUploadProgress}%` }}></div>
              </div>
            </div>
          )}

          {isAnalyzing && pendingAnalysis?.status === 'analyzing' && (
            <div className="loading-message background-analysis">
              <span className="loading-spinner"></span>
              <span>음성 파일 분석 중... (다른 페이지로 이동해도 분석이 계속됩니다)</span>
              <button
                type="button"
                className="cancel-analysis-btn"
                onClick={cancelAnalysis}
              >
                분석 취소
              </button>
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={!file || isAnalyzing}
          >
            {isAnalyzing ? '분석 중...' : '분석 시작'}
          </button>
        </form>

        {/* Results */}
        {results && (
          <div className="results-container">
            <div className="results-header">
              <div className="results-title-section">
                <h2 className="results-title">
                  {selectedItem?.title || selectedItem?.filename || '분석 결과'}
                </h2>
                {selectedItem && (
                  <div className="results-meta">
                    <span className="results-date">{formatDate(selectedItem.createdAt)}</span>
                    <button
                      className="results-lock-btn"
                      onClick={() => {
                        const item = history.find(h => h.id === selectedItem.id);
                        setPasswordModal({
                          show: true,
                          itemId: selectedItem.id,
                          mode: item?.hasPassword ? 'change' : 'set'
                        });
                      }}
                      data-tooltip={selectedItem.hasPassword ? '비밀번호 변경' : '비밀번호 설정'}
                    >
                      {selectedItem.hasPassword ? '🔒 비밀번호 변경' : '🔓 비밀번호 설정'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Keywords Section - moved to top */}
            {results.keywords && results.keywords.length > 0 && (
              <div className="keywords-top">
                {results.keywords.map((keyword, index) => (
                  <span key={index} className="keyword-tag">{keyword}</span>
                ))}
              </div>
            )}

            {/* Transcript Section */}
            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">
                  <span className="section-icon">💬</span>
                  전사 텍스트
                </h3>
                <button
                  type="button"
                  className="toggle-transcript-btn"
                  onClick={() => setShowTranscript(!showTranscript)}
                >
                  {showTranscript ? '접기' : '전사 텍스트 보기'}
                </button>
              </div>

              {showTranscript && (
                <div className="transcript-wrapper">
                  {/* Speaker List Panel */}
                  {getUniqueSpeakers().length > 0 && (
                    <div className="speaker-panel">
                      <div className="speaker-panel-header">
                        <span className="speaker-panel-title">등장인물</span>
                        <span className="speaker-panel-hint">클릭하여 이름 수정</span>
                      </div>
                      <div className="speaker-list">
                        {getUniqueSpeakers().map((speaker, index) => (
                          <div key={speaker} className="speaker-item">
                            {editingSpeaker === speaker ? (
                              <input
                                type="text"
                                className="speaker-edit-input"
                                defaultValue={getSpeakerDisplayName(speaker)}
                                autoFocus
                                onBlur={(e) => handleSpeakerNameChange(speaker, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSpeakerNameChange(speaker, e.target.value);
                                  if (e.key === 'Escape') setEditingSpeaker(null);
                                }}
                                placeholder="이름 입력"
                              />
                            ) : (
                              <button
                                className="speaker-name-btn"
                                onClick={() => setEditingSpeaker(speaker)}
                              >
                                <span className="speaker-color" style={{ backgroundColor: `hsl(${index * 60}, 70%, 50%)` }}></span>
                                <span className="speaker-display-name">{getSpeakerDisplayName(speaker)}</span>
                                {speakerNames[speaker] && (
                                  <span className="speaker-original">({speaker})</span>
                                )}
                                <span className="speaker-edit-icon">✎</span>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Compact Transcript */}
                  <div className="transcript-content compact">
                    {results.transcript && results.transcript.length > 0 ? (
                      results.transcript.map((item, index) => {
                        const speakerIndex = getUniqueSpeakers().indexOf(item.speaker);
                        return (
                          <div key={index} className="transcript-line">
                            <span className="line-time">{item.time || formatTimestamp(item.timestamp || 0)}</span>
                            <span
                              className="line-speaker"
                              style={{ color: `hsl(${speakerIndex * 60}, 70%, 40%)` }}
                            >
                              {getSpeakerDisplayName(item.speaker)}
                            </span>
                            <span className="line-text">{item.text}</span>
                          </div>
                        );
                      })
                    ) : (
                      <p className="transcript-text">{results.transcript}</p>
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* Key Points Section */}
            {results.keyPoints && results.keyPoints.length > 0 && (
              <section className="result-section">
                <h3 className="section-title">
                  <span className="section-icon">🎯</span>
                  핵심 포인트
                </h3>
                <ul className="key-points-list">
                  {results.keyPoints.map((point, index) => (
                    <li key={index} className="key-point-item">{point}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Summary Section */}
            {results.summary && (
              <section className="result-section">
                <h3 className="section-title">
                  <span className="section-icon">📝</span>
                  요약
                </h3>
                <p className="summary-text">{results.summary}</p>
              </section>
            )}
          </div>
        )}

        {/* Analysis History - moved below */}
        {(history.length > 0 || isAdmin) && (
          <section className="history-section">
            <div className="history-header-row">
              <div className="history-title-row">
                <h2 className="history-title">
                  {showAllUsers ? '전체 분석 결과' : '분석 결과'}
                </h2>
                {isAdmin && (
                  <label className="admin-toggle">
                    <input
                      type="checkbox"
                      checked={showAllUsers}
                      onChange={(e) => setShowAllUsers(e.target.checked)}
                    />
                    <span className="toggle-label">전체 사용자 보기</span>
                  </label>
                )}
              </div>
              <div className="history-controls">
                <div className="sort-buttons">
                  <button
                    className={`sort-btn ${sortBy === 'newest' ? 'active' : ''}`}
                    onClick={() => setSortBy('newest')}
                  >
                    최신순
                  </button>
                  <button
                    className={`sort-btn ${sortBy === 'oldest' ? 'active' : ''}`}
                    onClick={() => setSortBy('oldest')}
                  >
                    오래된순
                  </button>
                  <button
                    className={`sort-btn ${sortBy === 'title' ? 'active' : ''}`}
                    onClick={() => setSortBy('title')}
                  >
                    제목순
                  </button>
                </div>
                <div className="search-container">
                  <span className="search-icon">🔍</span>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="검색..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="search-clear"
                      onClick={() => setSearchQuery('')}
                      title="검색어 지우기"
                    >✕</button>
                  )}
                </div>
              </div>
            </div>
            {isLoadingHistory ? (
              <div className="history-loading">
                <span className="loading-spinner"></span>
                <span>불러오는 중...</span>
              </div>
            ) : filteredAndSortedHistory.length === 0 && !isAnalyzing ? (
              <div className="history-empty">
                <span className="empty-icon">🔎</span>
                <p>{searchQuery ? `"${searchQuery}"에 대한 검색 결과가 없습니다` : '분석 결과가 없습니다'}</p>
              </div>
            ) : (
              <div className="board-container">
                <table className={`board-table ${showAllUsers ? 'with-user' : ''}`}>
                  <thead>
                    <tr>
                      <th className="col-no">번호</th>
                      {showAllUsers && <th className="col-user">사용자</th>}
                      <th className="col-title">제목</th>
                      <th className="col-date">날짜</th>
                      <th className="col-actions">관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* 분석 중인 파일 표시 */}
                    {isAnalyzing && pendingAnalysis && (
                      <tr className="board-row analyzing-row">
                        <td className="col-no">-</td>
                        {showAllUsers && (
                          <td className="col-user">
                            <div className="user-info">
                              <span className="user-name">{user?.name || '나'}</span>
                            </div>
                          </td>
                        )}
                        <td className="col-title">
                          <div className="title-content analyzing">
                            <span className="analyzing-spinner"></span>
                            <span className="title-text">{pendingAnalysis.fileName}</span>
                            <span className="analyzing-status">
                              {pendingAnalysis.status === 'uploading'
                                ? `업로드 중... ${contextUploadProgress}%`
                                : '분석 진행 중...'}
                            </span>
                          </div>
                        </td>
                        <td className="col-date">-</td>
                        <td className="col-actions">
                          <button
                            className="action-btn cancel-btn"
                            onClick={cancelAnalysis}
                            title="분석 취소"
                          >✕</button>
                        </td>
                      </tr>
                    )}
                    {filteredAndSortedHistory.map((item, index) => (
                      <tr
                        key={item.id}
                        className={`board-row ${selectedItem?.id === item.id ? 'selected' : ''}`}
                        onClick={() => handleHistoryItemClick(item)}
                      >
                        <td className="col-no">{filteredAndSortedHistory.length - index}</td>
                        {showAllUsers && (
                          <td className="col-user">
                            <div className="user-info">
                              <span className="user-name">{item.userName || '알 수 없음'}</span>
                              <span className="user-email">{item.userEmail}</span>
                            </div>
                          </td>
                        )}
                        <td className="col-title">
                          {editingId === item.id ? (
                            <div className="title-edit-container" onClick={e => e.stopPropagation()}>
                              <input
                                type="text"
                                className="title-edit-input"
                                value={editingTitle}
                                onChange={(e) => setEditingTitle(e.target.value)}
                                placeholder="제목을 입력하세요"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveTitle(e, item.id);
                                  if (e.key === 'Escape') handleCancelEdit(e);
                                }}
                              />
                              <button
                                className="title-btn save"
                                onClick={(e) => handleSaveTitle(e, item.id)}
                                title="저장"
                              >✓</button>
                              <button
                                className="title-btn cancel"
                                onClick={handleCancelEdit}
                                title="취소"
                              >✕</button>
                            </div>
                          ) : (
                            <div className="title-content">
                              <span className="title-text">
                                {item.title || item.filename}
                                {item.hasPassword && <span className="lock-icon">🔒</span>}
                              </span>
                              {item.results?.keywords && item.results.keywords.length > 0 && (
                                <div className="title-keywords">
                                  {item.results.keywords.slice(0, 3).map((keyword, idx) => (
                                    <span key={idx} className="keyword-tag-sm">{keyword}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="col-date">{formatDate(item.createdAt)}</td>
                        <td className="col-actions" onClick={e => e.stopPropagation()}>
                          <div className="action-buttons">
                            <button
                              className="action-btn edit"
                              onClick={(e) => handleEditTitle(e, item)}
                              data-tooltip="제목 수정"
                            >✎</button>
                            <button
                              className="action-btn lock"
                              onClick={(e) => handleSetPassword(e, item.id)}
                              data-tooltip={item.hasPassword ? '비밀번호 변경' : '비밀번호 설정'}
                            >
                              {item.hasPassword ? '🔒' : '🔓'}
                            </button>
                            <button
                              className="action-btn delete"
                              onClick={(e) => handleDeleteItem(e, item.id)}
                              data-tooltip="삭제"
                            >🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* Password Modal */}
        {passwordModal.show && (
          <div className="modal-overlay" onClick={handlePasswordModalClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h3 className="modal-title">
                {passwordModal.mode === 'unlock' && '비밀번호 입력'}
                {passwordModal.mode === 'set' && '비밀번호 설정'}
                {passwordModal.mode === 'change' && '비밀번호 변경'}
              </h3>
              <p className="modal-description">
                {passwordModal.mode === 'unlock'
                  ? '이 항목을 보려면 비밀번호를 입력하세요.'
                  : '4자리 숫자를 입력하세요.'}
              </p>
              <input
                type="password"
                className="password-input"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder={passwordModal.mode === 'unlock' ? '4자리 숫자' : '비밀번호 (4자리 숫자)'}
                maxLength={4}
                pattern="\d{4}"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && passwordModal.mode === 'unlock') handlePasswordSubmit();
                  if (e.key === 'Escape') handlePasswordModalClose();
                }}
              />
              {passwordModal.mode !== 'unlock' && (
                <input
                  type="password"
                  className="password-input"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder="비밀번호 확인"
                  maxLength={4}
                  pattern="\d{4}"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handlePasswordSubmit();
                    if (e.key === 'Escape') handlePasswordModalClose();
                  }}
                />
              )}
              <div className="modal-actions">
                <button className="modal-btn cancel" onClick={handlePasswordModalClose}>
                  취소
                </button>
                <button className="modal-btn confirm" onClick={handlePasswordSubmit}>
                  확인
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioAnalysis;
